import React from "react";
import { useState, useEffect } from "react";
import Cookies from "js-cookie";
import {
  Button,
  Collapse,
  DatePicker,
  Divider,
  Input,
  InputNumber,
  Modal,
  Space,
  message,
} from "antd";
import { endOfDay, format } from "date-fns";
import { saveAs } from "file-saver";
import { addMonths, isValid } from "date-fns"; // Use date-fns for date manipulation
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { tokens } from "../../theme";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

const PRIMARY_COLOR = "#7057F6";
const TEXT_COLOR = "#14213F";
const MUTED_COLOR = "#68748C";
const BORDER_COLOR = "#E3E6F6";
const GLASS_CARD_BACKGROUND =
  "linear-gradient(145deg, rgba(255,255,255,0.88), rgba(240,238,255,0.72))";
const OVERDUE_CARD_BACKGROUND =
  "linear-gradient(135deg, rgba(204,58,138,0.14), rgba(255,255,255,0.95))";
const CARD_BORDER = `1px solid ${BORDER_COLOR}`;
const CARD_SHADOW =
  "0 12px 30px -22px rgba(36, 55, 102, 0.35), 0 2px 8px rgba(36, 55, 102, 0.06)";

const MODAL_BODY_STYLE = {
  padding: 0,
  background: "transparent",
};

const MODAL_MASK_STYLE = {
  backdropFilter: "blur(8px)",
  backgroundColor: "rgba(20,33,63,0.38)",
};

const GLASS_MODAL_PANEL_STYLE = {
  background: "linear-gradient(145deg, #FFFFFF, #F7F5FF)",
  borderRadius: 24,
  border: CARD_BORDER,
  boxShadow: "0 32px 90px -46px rgba(20, 33, 63, 0.65)",
  color: TEXT_COLOR,
  position: "relative",
  overflow: "hidden",
};

const GLASS_MODAL_STYLES = {
  padding: "0px",
  content: {
    background: "transparent",
    padding: 0,
    boxShadow: "none",
  },
  body: {
    padding: 0,
  },
};

const INITIAL_LOAN_CREATE_STATE = {
  principal_name: "",
  amount: 0,
  tenure: 1,
  repayments: [],
  start_date: "",
  end_date: "",
  email: "",
  mobile: "",
  loanAmount: "",
  interest: "",
  processingFee: "",
  ref_code: "",
};

const INITIAL_CHARGE_DATA = {
  id: "",
  payerSwiftBic: "",
  payerSegment: "",
  payerName: "",
  precheckMinAmount: 1,
  precheckMaxAmount: 0,
  precheckCurrency: "SGD",
  endDate: "",
  returnUrl: "https://smart-lend.com/a-manual-giro",
  accountNumber: "",
  lender_id: "",
  loan_id: "",
  repay_id: "",
};

const PRIMARY_BUTTON_STYLE = {
  backgroundColor: PRIMARY_COLOR,
  borderColor: PRIMARY_COLOR,
  color: "#fff",
  borderRadius: 999,
  fontWeight: 800,
  height: 40,
};

const SECONDARY_BUTTON_STYLE = {
  borderColor: BORDER_COLOR,
  color: TEXT_COLOR,
  borderRadius: 999,
  fontWeight: 800,
  height: 40,
  background: "#FFFFFF",
};

const FIELD_LABEL_STYLE = {
  fontSize: "12px",
  fontWeight: 800,
  color: MUTED_COLOR,
  marginBottom: 6,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const FIELD_HELP_TEXT_STYLE = {
  fontSize: "12px",
  color: MUTED_COLOR,
  fontWeight: 700,
};

const INPUT_STYLE = {
  borderRadius: 14,
  padding: "11px 12px",
  background: "#FAFBFF",
  border: CARD_BORDER,
  color: TEXT_COLOR,
  fontWeight: 800,
};

const SECTION_TITLE_STYLE = {
  fontSize: "15px",
  fontWeight: 900,
  color: TEXT_COLOR,
};

const GlassModal = ({
  open,
  onClose,
  children,
  actions,
  contentPadding = 24,
  showCloseButton = true,
  headerTitle,
  headerSubtitle,
  headerMark = "ML",
  ...modalProps
}) => (
  <Modal
    open={open}
    onCancel={onClose}
    title={null}
    footer={null}
    bodyStyle={MODAL_BODY_STYLE}
    maskStyle={MODAL_MASK_STYLE}
    rootClassName="glass-modal sl-manual-modal-root"
    className="glass-modal sl-loan-action-modal sl-manual-modal"
    styles={GLASS_MODAL_STYLES}
    closable={false}
    {...modalProps}
  >
    <div className="sl-manual-modal-panel" style={GLASS_MODAL_PANEL_STYLE}>
      {showCloseButton && onClose ? (
        <button
          type="button"
          className="glass-modal__close sl-manual-modal-close"
          onClick={onClose}
        >
          <CloseRoundedIcon fontSize="small" />
        </button>
      ) : null}
      {headerTitle ? (
        <div className="sl-loan-modal-head sl-manual-modal-head">
          <div className="sl-loan-modal-title">
            <div className="sl-loan-modal-mark">{headerMark}</div>
            <div>
              <h2>{headerTitle}</h2>
              {headerSubtitle ? <p>{headerSubtitle}</p> : null}
            </div>
          </div>
        </div>
      ) : null}
      <div style={{ padding: contentPadding }}>
        {children}
        {actions ? <div className="glass-modal__actions">{actions}</div> : null}
      </div>
    </div>
  </Modal>
);

const TestCharge = () => {
  const [data, setData] = useState([]);
  const [openedLoans, setOpenedLoans] = useState(null);
  const [checkboxChecked, setCheckboxChecked] = useState([]);
  const [createLoanModalIsOpen, setCreateLoanModalIsOpen] = useState(false);
  const [filterBy, setFilterBy] = useState("");
  const [loanCreateData, setLoanCreateData] = useState(
    INITIAL_LOAN_CREATE_STATE
  );
  const [createLoanSubmitting, setCreateLoanSubmitting] = useState(false);
  const [chargeData, setChargeData] = useState({ ...INITIAL_CHARGE_DATA });

  const [clickedPaymentRow, setClickedPaymentRow] = useState(null);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [paramValue, setParamValue] = useState(""); // Extract the "param" value

  useEffect(() => {
    if (searchParams.get("search")) {
      setParamValue(searchParams.get("search"));
    } else {
      setParamValue("");
    }
  }, [searchParams.get("search")]);
  const theme = useTheme();

  const colors = tokens(theme.palette.mode);
  const isDesktop = useMediaQuery("(min-width:1200px)");
  const isModalTwoColumn = useMediaQuery("(min-width:900px)");
  const isMobile = useMediaQuery("(max-width:600px)");

  const DateDifference = (repa) => {
    if (!repa?.date || !repa?.payment_date) {
      return <></>;
    }

    const paymentDate = new Date(repa.payment_date);
    const userPaidDate = new Date(repa.date);

    const differenceInMs = userPaidDate - paymentDate;
    const differenceInDays = Math.ceil(differenceInMs / (1000 * 60 * 60 * 24));

    const isLate = differenceInDays > 0;
    const displayDiff = isLate
      ? `-${differenceInDays}`
      : `+${Math.abs(differenceInDays)}`;

    return (
      <div
        style={{
          width: "50%",
          fontWeight: "bold",
          color: isLate ? "red" : "green",
        }}
      >
        {displayDiff} days
      </div>
    );
  };

  const exportToExcel = () => {
    if (!data || data.length === 0) return;

    const processedData = data.map(
      ({
        acc_number,
        amount,
        email,
        end_date,
        id,
        interest,
        loanAmount,
        max_amount,
        min_amount,
        mobile,
        principal_name,
        processingFee,
        ref_number,
        segment,
        start_date,
        tenure,
      }) => ({
        "Monthly Installment": Number(amount)?.toLocaleString("en-US", {
          style: "currency",
          currency: "SGD",
        }),
        Email: email,
        Interest: `${interest}%`,
        "Loan Amount": Number(loanAmount)?.toLocaleString("en-US", {
          style: "currency",
          currency: "SGD",
        }),
        Mobile: mobile,
        "Principal Name": principal_name,
        "Processing Fee": `${processingFee}%`,
        "Start Date": start_date,
        Tenure: `${tenure} months`,
      })
    );

    const worksheet = XLSX.utils.json_to_sheet(processedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Loan Data");
    XLSX.writeFile(workbook, "loan_data.xlsx");
  };

  const updateLoanPaymentStatus = async (datatopush) => {
    try {
      const response = await fetch(
        `https://www.smart-lend.com/api/giro/updateManualGiro`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify(datatopush),
        }
      );
      const json = await response.json();
      console.log(json);
      if (response.status === 200) {
        message.success("Giro payment succesfully updated");
        const updatingData = [...data];
        updatingData[openedLoans].repayments = JSON.parse(
          datatopush.repayments
        );
        setData(updatingData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const [changeEmailModalIsopen, setChangeEmailModalIsOpen] = useState(false);
  const [changeEmailInput, setChangeEmailInput] = useState("");
  const [updateEmailLoading, setUpdateEmailLoading] = useState(false);
  const changeManualLoanEmail = async (sam) => {
    try {
      setUpdateEmailLoading(true);
      const response = await fetch(
        `https://www.smart-lend.com/api/giro/changeManualLoanEmail`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({ id: sam.id, email: sam.email }),
        }
      );
      const json = await response.json();

      if (response.ok) {
        const refIndex = data.findIndex((loans) => loans.id === sam.id);
        if (refIndex > -1) {
          const updatingData = [...data];
          updatingData[refIndex].email = sam.email;
          setData(updatingData);
        }
        setChangeEmailInput("");
        setChangeEmailModalIsOpen(false);
        message.success("Borrower email succesfully changed!");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setUpdateEmailLoading(false);
    }
  };

  const [changeRefModalIsopen, setChangeRefModalIsOpen] = useState(false);
  const [changeRefInput, setChangeRefInput] = useState("");
  const [updateRefLoading, setUpdateRefLoading] = useState(false);
  const changeManualLoanRef = async (sam) => {
    try {
      setUpdateRefLoading(true);
      const response = await fetch(
        `https://www.smart-lend.com/api/giro/changeManualLoanRef`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({ id: sam.id, ref_code: sam.ref_code }),
        }
      );
      const json = await response.json();

      if (response.ok) {
        const refIndex = data.findIndex((loans) => loans.id === sam.id);
        if (refIndex > -1) {
          const updatingData = [...data];
          updatingData[refIndex].ref_code = sam.ref_code;
          setData(updatingData);
        }
        setChangeRefInput("");
        setChangeRefModalIsOpen(false);
        message.success("Borrower ref succesfully changed!");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setUpdateRefLoading(false);
    }
  };

  const deleteManualLoan = async (id) => {
    try {
      const response = await fetch(
        `https://www.smart-lend.com/api/giro/deleteManualLoan`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({ id: id }),
        }
      );
      const json = await response.json();
      if (response.status === 200) {
        message.success("Loan Succesfully deleted");
        setOpenedLoans(null);
        setChargeData({ ...INITIAL_CHARGE_DATA });
        const parsedData = json.data.map((item) => ({
          ...item,
          repayments: JSON.parse(item.repayments), // Parse the repayments field
        }));

        setData(parsedData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const creategiroauth = async () => {
    console.log(chargeData);
    try {
      const response = await fetch(
        `https://www.smart-lend.com/api/giro/creategiroauth`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: JSON.stringify({
            ...chargeData,
            precheckMaxAmount: parseFloat(
              (chargeData.precheckMaxAmount * 100).toFixed(0)
            ),
          }),
        }
      );
      const json = await response.json();
      console.log(json);
      if (response.status === 201) {
        message.success(json.message);
        if (json?.api_response?.authorizeUrl) {
          Cookies.set(
            "ddtokenauth",
            JSON.stringify({
              ddid: json.api_response.id,
              loan_id: chargeData.loan_id,
              lender_id: chargeData.lender_id,
              repay_id: chargeData.repay_id,
            })
          );
          window.location.href = json?.api_response?.authorizeUrl;
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const createManualGiro = async () => {
    try {
      setCreateLoanSubmitting(true);
      const datatosend = {
        principal_name: loanCreateData.principal_name,
        amount: loanCreateData.amount,
        tenure: loanCreateData.tenure,
        repayments: JSON.stringify(loanCreateData.repayments),
        start_date: loanCreateData.start_date,
        end_date: loanCreateData.end_date,
        email: loanCreateData.email,
        mobile: loanCreateData.mobile,
        interest: loanCreateData.interest,
        processingFee: loanCreateData.processingFee,
        loanAmount: loanCreateData.loanAmount,
        ref_code: loanCreateData.ref_code,
      };
      const response = await fetch(
        "https://www.smart-lend.com/api/giro/createManualGiro",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(datatosend),
        }
      );
      if (response.status === 201) {
        const json = await response.json();
        const datatopush = {
          ...json.manualGiroData,
          repayments: JSON.parse(json.manualGiroData.repayments),
        };
        setData([...data, datatopush]);
        setCreateLoanModalIsOpen(false);
        setLoanCreateData({ ...INITIAL_LOAN_CREATE_STATE });
        message.success("Manual loan schedule created successfully");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCreateLoanSubmitting(false);
    }
  };
  const checkGiroAuth = async (token) => {
    console.log(chargeData);
    try {
      const response = await fetch(
        `https://www.smart-lend.com/api/giro/checkGiroAuth`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ddid: token.ddid }),
        }
      );
      if (response.ok) {
        Cookies.remove("ddtokenauth");
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const ddtoken = Cookies.get("ddtokenauth");
    if (ddtoken) {
      const ddData = JSON.parse(ddtoken);
      checkGiroAuth(ddData);
    }
  }, []);

  const geAllManualGiro = async () => {
    try {
      const response = await fetch(
        "https://www.smart-lend.com/api/giro/geAllManualGiro",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        const json = await response.json();

        // Parse the repayments field in each item
        const parsedData = json.data.map((item) => ({
          ...item,
          repayments: JSON.parse(item.repayments), // Parse the repayments field
        }));

        setData(parsedData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    geAllManualGiro();
  }, []);

  useEffect(() => {
    if (
      loanCreateData.amount &&
      parseFloat(loanCreateData.amount) > 0 &&
      loanCreateData.tenure &&
      loanCreateData.start_date
    ) {
      const generateRepayments = (startDate, amount, tenure) => {
        const repayments = [];
        const monthlyAmount = parseFloat(amount);

        for (let i = 0; i < tenure; i++) {
          // Create a date object for the start date
          const baseDate = new Date(startDate);

          // Add months while maintaining the same day or the last valid day of the month
          const paymentDate = addMonths(baseDate, i);
          const lastDayOfMonth = new Date(
            paymentDate.getFullYear(),
            paymentDate.getMonth() + 1,
            0
          ).getDate(); // Get the last day of the current month
          const adjustedDate = Math.min(baseDate.getDate(), lastDayOfMonth);

          // Set the adjusted date to paymentDate
          paymentDate.setDate(adjustedDate);

          // Add the payment details to the array
          repayments.push({
            amount: monthlyAmount.toFixed(2), // Round to two decimal places
            payment_date: paymentDate.toISOString().split("T")[0], // Format to YYYY-MM-DD
            status: 0,
          });
        }

        return repayments;
      };

      // Generate repayments
      const repayments = generateRepayments(
        loanCreateData.start_date,
        loanCreateData.amount,
        loanCreateData.tenure
      );

      // Calculate the end date and add 7 days
      const calculatedEndDate =
        repayments[repayments.length - 1]?.payment_date || "";
      const adjustedEndDate = calculatedEndDate
        ? new Date(calculatedEndDate)
        : null;

      if (adjustedEndDate) {
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 7); // Add 7 days
      }

      // Update the loan data
      setLoanCreateData((prevData) => ({
        ...prevData,
        repayments: repayments,
        end_date: adjustedEndDate
          ? adjustedEndDate.toISOString().split("T")[0] // Format as YYYY-MM-DD
          : "",
      }));
      console.log(loanCreateData);
    }
  }, [loanCreateData.amount, loanCreateData.tenure, loanCreateData.start_date]);

  const getPreviousMonthDate = (dateString) => {
    // Convert to a Date object
    let date = new Date(dateString);

    // Set to one month before
    date.setMonth(date.getMonth() - 1);

    // Format it back to YYYY-MM-DD
    let formattedDate = date.toISOString().split("T")[0];

    return formattedDate;
  };
  const formatDate = (dateString) => {
    if (!dateString) return "-"; // Return a default value if the date string is not provided
    const date = new Date(dateString); // Create a Date object from the date string
    if (isValid(dateString)) {
      const formattedDate = format(date, "MMM dd, yyyy"); // Use date-fns to format the date in the desired format

      return formattedDate;
    } else {
      return "-";
    }
  };

  function generateDate() {
    const today = new Date(); // Get the current date
    const day = String(today.getDate()).padStart(2, "0"); // Get the day and ensure 2 digits
    const month = String(today.getMonth() + 1).padStart(2, "0"); // Get the month and ensure 2 digits
    const year = today.getFullYear(); // Get the year

    return `${day}${month}${year}`; // Concatenate into ddmmyyyy
  }

  function generateDate2() {
    const today = new Date(); // Get the current date
    const day = String(today.getDate()).padStart(2, "0"); // Get the day and ensure 2 digits
    const month = String(today.getMonth() + 1).padStart(2, "0"); // Get the month and ensure 2 digits
    const year = today.getFullYear(); // Get the year

    return `${year}${month}${day}`; // Concatenate into ddmmyyyy
  }

  const handleCheckboxChange = (index) => {
    setCheckboxChecked((prev) => {
      if (prev.includes(index)) {
        // Remove index if already checked
        return prev.filter((i) => i !== index);
      } else {
        // Add index if not already checked
        return [...prev, index];
      }
    });
  };

  function generateRandomId() {
    return Math.floor(100 + Math.random() * 900); // Generate a random number between 100 and 999
  }
  const headerTemplate = [
    { text: "20", length: 2, align: "left", description: "Collection code" },
    {
      text: `${generateRandomId()}`,
      length: 3,
      align: "left",
      description: "Batch number random id",
    },
    {
      text: generateDate2(),
      length: 8,
      align: "left",
      description: "Date of generating document YYYYMMDD",
    },
    {
      text: "OCBCSGSGXXX",
      length: 11,
      align: "left",
      description: "Originating bank code",
    },
    {
      text: "602180564001",
      length: 34,
      align: "left",
      description: "Account number",
    },
    { text: "", length: 3, align: "left", description: "Blank filler" },
    {
      text: "On Behalf of SmartL",
      length: 20,
      align: "left",
      description: "On behalf of",
    },
    { text: "", length: 120, align: "left", description: "Blank filler" },
    { text: "", length: 4, align: "left", description: "Blank filler" },
    {
      text: "FAST",
      length: 4,
      align: "left",
      description: "Clearing using GIRO of FAST",
    },
    { text: "", length: 16, align: "left", description: "Blank filler" },
    {
      text: generateDate(),
      length: 8,
      align: "left",
      description: "Date Transaction should happen DDMMYYYY",
    },
    {
      text: "",
      length: 4,
      align: "left",
      description: "Leave blank for immediate",
    },
    { text: "", length: 1, align: "left", description: "Batch booking filler" },
    { text: "", length: 762, align: "left", description: "Blank filler" },
  ];
  const getrepaymentindex = (id) => {
    // Find the loan index
    const loanIndex = data.findIndex((dat) => dat.id === id);

    if (loanIndex > -1) {
      // Get the repayments array for the loan
      const repayments = data[loanIndex].repayments;

      // Find the earliest repayment with status === 0
      let earliestIndex = -1;
      let earliestDate = null;

      repayments.forEach((repayment, index) => {
        if (repayment.status === 0) {
          const paymentDate = new Date(repayment.payment_date);
          if (!earliestDate || paymentDate < earliestDate) {
            earliestDate = paymentDate;
            earliestIndex = index;
          }
        }
      });

      // Return the index + 1 of the earliest repayment with status === 0
      return earliestIndex > -1 ? earliestIndex + 1 : -1; // Return -1 if none found
    }

    // Return -1 if the loan with the given id is not found
    return -1;
  };
  const generateFixedWidthTxt = () => {
    let secondLineOnwards = [];
    const dataAvailableForGen = data.filter(
      (dat) =>
        dat.status === 1 &&
        dat.repayments.filter((repa) => repa.status === 0).length > 0
    );
    checkboxChecked.map((key, index) => {
      const dataToPush = [
        {
          text: dataAvailableForGen?.[key]?.swift_bic,
          length: 11,
          align: "left",
          description: "Bank Code",
        },
        {
          text: dataAvailableForGen?.[key]?.acc_number,
          length: 34,
          align: "left",
          description: "Employee account number",
        },
        {
          text: dataAvailableForGen?.[key]?.name,
          length: 35,
          align: "left",
          description: "Employee name",
        },
        {
          text: "",
          length: 105,
          align: "left",
          description:
            "Blank filler for name as FAST only allow up till 35 char",
        },
        { text: "SGD", length: 3, align: "left", description: "Currency" },
        {
          text: String(dataAvailableForGen?.[key]?.amount * 100)
            .replace(".", "")
            .padStart(17, "0"),
          length: 17,
          align: "left",
          description: "Currency in cents",
        },
        {
          text: `SML ${getrepaymentindex(dataAvailableForGen?.[key]?.id)}/${
            dataAvailableForGen?.[key]?.tenure
          } repayment`,
          length: 35,
          align: "left",
          description: "Payment Details",
        },
        {
          text: "COLL",
          length: 4,
          align: "left",
          description: "Collection code",
        },
        {
          text: dataAvailableForGen?.[key]?.ref_number,
          length: 35,
          align: "right",
          description: "Debtor Reference",
        },
        { text: "", length: 140, align: "left", description: "Blank filler" },
        { text: "", length: 140, align: "left", description: "Blank filler" },
        {
          text: "",
          length: 1,
          align: "left",
          description: "Blank filler (Send remitance via)",
        },
        {
          text: "",
          length: 255,
          align: "left",
          description: "Blank filler (Send details only if invoice involved)",
        },
        {
          text: "",
          length: 12,
          align: "left",
          description: "Blank filler (Proxy type only for paynow transactions)",
        },
        {
          text: "",
          length: 140,
          align: "left",
          description:
            "Blank filler (Proxy value only for paynow transactions)",
        },
        { text: "", length: 33, align: "left", description: "Blank filler" },
      ];
      console.log(dataToPush);
      secondLineOnwards.push(dataToPush);
    });

    console.log([[...headerTemplate], ...secondLineOnwards]);

    const totalData = [[...headerTemplate], ...secondLineOnwards];

    const lines = totalData.map(
      (line) =>
        line
          .map((col) => {
            const { text, length, align } = col;

            // Truncate if text exceeds length
            const truncated =
              text.length > length ? text.slice(0, length) : text;

            // Pad based on alignment
            if (align === "left") {
              return truncated.padEnd(length, " ");
            } else if (align === "right") {
              return truncated.padStart(length, " ");
            } else {
              throw new Error("Invalid alignment value");
            }
          })
          .join("") // Combine all columns into a single line
    );

    // Combine all lines with newline characters
    const result = lines.join("\n");
    const formatDateForFilename = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");

      return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
    };
    // Create and save the .txt file
    const blob = new Blob([result], { type: "text/plain;charset=utf-8" });
    saveAs(blob, `giro_${formatDateForFilename(new Date())}.txt`);
    message.success("Giro document created.");
  };
  const [viewingTab, setViewingTab] = useState(0);

  const getTotalRepayment = (repayments) => {
    return repayments.reduce(
      (total, dat) => total + parseFloat(dat.amount || 0),
      0
    );
  };

  const getNextRepaymentDate = (loan = {}) => {
    const outstandingRepayments =
      loan?.repayments?.filter((repay) => repay.status === 0) || [];
    if (!outstandingRepayments.length) {
      return null;
    }

    const nextPaymentDate = outstandingRepayments[0]?.payment_date
      ? new Date(outstandingRepayments[0].payment_date)
      : null;

    if (!nextPaymentDate || Number.isNaN(nextPaymentDate.getTime())) {
      return null;
    }

    return nextPaymentDate;
  };

  const isLoanOverdue = (loan) => {
    const nextPaymentDate = getNextRepaymentDate(loan);
    if (!nextPaymentDate) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return nextPaymentDate < today;
  };

  const getManualLoanRowStyle = (loan, overdueOverride) => {
    const overdue =
      typeof overdueOverride === "boolean"
        ? overdueOverride
        : isLoanOverdue(loan);

    return {
      position: "relative",
      overflow: "hidden",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "18px 20px",
      margin: "12px 0",
      background: overdue ? OVERDUE_CARD_BACKGROUND : GLASS_CARD_BACKGROUND,
      color: TEXT_COLOR,
      fontFamily: "Manrope, sans-serif",
      fontWeight: 500,
      fontSize: 12,
      cursor: "pointer",
      gap: "1%",
      borderRadius: 16,
      border: overdue ? "1px solid rgba(231,76,60,0.3)" : CARD_BORDER,
      boxShadow: CARD_SHADOW,
      backdropFilter: "blur(8px)",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
    };
  };

  const handleCloseCreateModal = () => {
    setCreateLoanModalIsOpen(false);
    setLoanCreateData({ ...INITIAL_LOAN_CREATE_STATE });
  };

  const canCreateLoanSchedule = Boolean(
    loanCreateData.principal_name &&
      loanCreateData.email &&
      loanCreateData.mobile &&
      loanCreateData.loanAmount &&
      loanCreateData.amount &&
      loanCreateData.tenure &&
      loanCreateData.start_date &&
      loanCreateData.repayments.length > 0
  );

  const modalGridColumns = isModalTwoColumn
    ? "repeat(2, minmax(0, 1fr))"
    : "1fr";

  const handleCloseChangeRefModal = () => {
    setChangeRefModalIsOpen(false);
    setChangeRefInput("");
  };

  const handleCloseChangeEmailModal = () => {
    setChangeEmailModalIsOpen(false);
    setChangeEmailInput("");
  };

  const handleUpdateRef = () => {
    if (openedLoans === null || !data[openedLoans]) return;
    changeManualLoanRef({
      id: data[openedLoans].id,
      ref_code: changeRefInput,
    });
  };

  const handleUpdateEmail = () => {
    if (openedLoans === null || !data[openedLoans]) return;
    changeManualLoanEmail({
      id: data[openedLoans].id,
      email: changeEmailInput,
    });
  };

  const canUpdateRef = Boolean(changeRefInput?.trim()?.length);
  const canUpdateEmail = Boolean(changeEmailInput?.trim()?.length);

  const handleCloseLoanDetails = () => {
    setOpenedLoans(null);
    setClickedPaymentRow(null);
    setChargeData({ ...INITIAL_CHARGE_DATA });
    setViewingTab(0);
  };

  return (
    <div
      className="sl-manual-page"
      style={{
        width: "100%",
        minHeight: "calc(100vh - 80px)",
        display: "flex",
        flexDirection: isDesktop ? "row" : "column",
        justifyContent: "space-between",
        gap: isMobile ? "16px" : "20px",
        color: "black",
      }}
    >
      <GlassModal
        open={createLoanModalIsOpen}
        onClose={handleCloseCreateModal}
        width={isDesktop ? "min(980px, 72vw)" : isMobile ? "96vw" : "92vw"}
        zIndex={800}
        style={{ top: 60 }}
        contentPadding={28}
        headerTitle="Create manual payment schedule"
        headerSubtitle="Fill in the loan and borrower details to generate a repayment plan."
        headerMark="CG"
        actions={
          <>
            <Button
              onClick={handleCloseCreateModal}
              style={SECONDARY_BUTTON_STYLE}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={createManualGiro}
              loading={createLoanSubmitting}
              style={PRIMARY_BUTTON_STYLE}
              disabled={!canCreateLoanSchedule}
            >
              Create schedule
            </Button>
          </>
        }
      >
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <div>
            <div style={SECTION_TITLE_STYLE}>Borrower details</div>
            <div style={FIELD_HELP_TEXT_STYLE}>
              These details appear in borrower reminders and statements.
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: modalGridColumns,
              gap: 16,
            }}
          >
            <div>
              <div style={FIELD_LABEL_STYLE}>Reference code</div>
              <Input
                allowClear
                placeholder="Optional"
                value={loanCreateData.ref_code}
                onChange={(event) =>
                  setLoanCreateData((prev) => ({
                    ...prev,
                    ref_code: event.target.value,
                  }))
                }
                style={INPUT_STYLE}
              />
            </div>
            <div>
              <div style={FIELD_LABEL_STYLE}>Borrower name</div>
              <Input
                placeholder="e.g. Jane Tan"
                value={loanCreateData.principal_name}
                onChange={(event) =>
                  setLoanCreateData((prev) => ({
                    ...prev,
                    principal_name: event.target.value,
                  }))
                }
                style={INPUT_STYLE}
              />
            </div>
            <div>
              <div style={FIELD_LABEL_STYLE}>Borrower email</div>
              <Input
                type="email"
                placeholder="name@email.com"
                value={loanCreateData.email}
                onChange={(event) =>
                  setLoanCreateData((prev) => ({
                    ...prev,
                    email: event.target.value,
                  }))
                }
                style={INPUT_STYLE}
              />
            </div>
            <div>
              <div style={FIELD_LABEL_STYLE}>Borrower mobile</div>
              <Input
                type="tel"
                placeholder="e.g. +65 8123 4567"
                value={loanCreateData.mobile}
                onChange={(event) =>
                  setLoanCreateData((prev) => ({
                    ...prev,
                    mobile: event.target.value,
                  }))
                }
                style={INPUT_STYLE}
              />
            </div>
          </div>

          <Divider style={{ margin: "0" }} />

          <div>
            <div style={SECTION_TITLE_STYLE}>Loan terms</div>
            <div style={FIELD_HELP_TEXT_STYLE}>
              Adjust any field to instantly refresh the repayment plan.
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: modalGridColumns,
              gap: 16,
            }}
          >
            <div>
              <div style={FIELD_LABEL_STYLE}>Loan amount</div>
              <Input
                type="number"
                prefix="S$"
                min={0}
                value={loanCreateData.loanAmount}
                onChange={(event) =>
                  setLoanCreateData((prev) => ({
                    ...prev,
                    loanAmount: event.target.value,
                  }))
                }
                style={INPUT_STYLE}
              />
            </div>
            <div>
              <div style={FIELD_LABEL_STYLE}>Interest rate (% p.a.)</div>
              <Input
                type="number"
                suffix="%"
                min={0}
                value={loanCreateData.interest}
                onChange={(event) =>
                  setLoanCreateData((prev) => ({
                    ...prev,
                    interest: event.target.value,
                  }))
                }
                style={INPUT_STYLE}
              />
            </div>
            <div>
              <div style={FIELD_LABEL_STYLE}>Processing fee (%)</div>
              <Input
                type="number"
                suffix="%"
                min={0}
                value={loanCreateData.processingFee}
                onChange={(event) =>
                  setLoanCreateData((prev) => ({
                    ...prev,
                    processingFee: event.target.value,
                  }))
                }
                style={INPUT_STYLE}
              />
            </div>
            <div>
              <div style={FIELD_LABEL_STYLE}>Monthly installment</div>
              <Input
                type="number"
                prefix="S$"
                min={0}
                value={loanCreateData.amount}
                onChange={(event) =>
                  setLoanCreateData((prev) => ({
                    ...prev,
                    amount: event.target.value,
                  }))
                }
                style={INPUT_STYLE}
              />
            </div>
            <div>
              <div style={FIELD_LABEL_STYLE}>Tenure (months)</div>
              <Input
                type="number"
                min={1}
                value={loanCreateData.tenure}
                onChange={(event) =>
                  setLoanCreateData((prev) => ({
                    ...prev,
                    tenure: event.target.value,
                  }))
                }
                style={INPUT_STYLE}
              />
            </div>
            <div>
              <div style={FIELD_LABEL_STYLE}>First payment date</div>
              <DatePicker
                style={{ ...INPUT_STYLE, width: "100%" }}
                format="YYYY-MM-DD"
                value={
                  loanCreateData.start_date
                    ? dayjs(loanCreateData.start_date)
                    : null
                }
                onChange={(date) =>
                  setLoanCreateData((prev) => ({
                    ...prev,
                    start_date: date ? date.format("YYYY-MM-DD") : "",
                  }))
                }
                allowClear
              />
            </div>
          </div>

          {loanCreateData.repayments.length > 0 && (
            <>
              <Divider style={{ margin: "0" }} />
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={SECTION_TITLE_STYLE}>Payment schedule</div>
                  <div style={FIELD_HELP_TEXT_STYLE}>
                    Amount changes cascade to future installments automatically.
                  </div>
                </div>
                <Space size={12} wrap>
                  <div style={FIELD_HELP_TEXT_STYLE}>
                    Tenure: {loanCreateData.tenure || 0} months
                  </div>
                  <div style={FIELD_HELP_TEXT_STYLE}>
                    Total scheduled: S$
                    {getTotalRepayment(loanCreateData.repayments)
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </div>
                  {loanCreateData.end_date && (
                    <div style={FIELD_HELP_TEXT_STYLE}>
                      Ends {formatDate(loanCreateData.end_date)}
                    </div>
                  )}
                </Space>
              </div>

              <div
                style={{
                  border: "1px solid rgba(112,87,246,0.1)",
                  borderRadius: 14,
                  overflow: "hidden",
                  boxShadow: "0 16px 32px rgba(112,87,246,0.08)",
                  background: "rgba(255,255,255,0.85)",
                  maxHeight: "320px",
                  overflowY: "auto",
                }}
              >
                {loanCreateData.repayments.map((dat, index) => {
                  const isLastRow =
                    index === loanCreateData.repayments.length - 1;
                  return (
                    <div
                      key={dat.payment_date + index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "14px 18px",
                        borderBottom: isLastRow
                          ? "none"
                          : "1px solid rgba(112,87,246,0.08)",
                        background:
                          index % 2 === 0
                            ? "rgba(112,87,246,0.05)"
                            : "rgba(255,255,255,0.95)",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: PRIMARY_COLOR,
                          }}
                        >
                          Installment {index + 1}
                        </div>
                        <div style={FIELD_HELP_TEXT_STYLE}>
                          Due {formatDate(dat?.payment_date)}
                        </div>
                      </div>
                      <InputNumber
                        min={0}
                        style={{ width: 160 }}
                        value={
                          dat?.amount !== undefined && dat?.amount !== null
                            ? Number(parseFloat(dat.amount).toFixed(2))
                            : 0
                        }
                        formatter={(value) => {
                          if (
                            value === undefined ||
                            value === null ||
                            value === ""
                          ) {
                            return "";
                          }
                          const numeric =
                            typeof value === "number" ? value : Number(value);
                          if (Number.isNaN(numeric)) {
                            return "";
                          }
                          return `S$ ${numeric.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`;
                        }}
                        parser={(value = "") => value.replace(/S\$\s?|,/g, "")}
                        onChange={(value) => {
                          const sanitizedValue = Number(value ?? 0);
                          setLoanCreateData((prevData) => {
                            const updatedRepayments = prevData.repayments.map(
                              (repayment, i) =>
                                i >= index
                                  ? {
                                      ...repayment,
                                      amount: sanitizedValue.toFixed(2),
                                    }
                                  : repayment
                            );

                            return {
                              ...prevData,
                              repayments: updatedRepayments,
                            };
                          });
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Space>
      </GlassModal>

      <GlassModal
        width={isMobile ? "90vw" : 400}
        open={changeRefModalIsopen}
        zIndex={802}
        onClose={handleCloseChangeRefModal}
        contentPadding={24}
        headerTitle="Update reference code"
        headerSubtitle="Give this manual loan a short identifier for quick reference."
        headerMark="RF"
        actions={
          <>
            <Button
              onClick={handleCloseChangeRefModal}
              style={SECONDARY_BUTTON_STYLE}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleUpdateRef}
              style={PRIMARY_BUTTON_STYLE}
              disabled={!canUpdateRef}
              loading={updateRefLoading}
            >
              Save changes
            </Button>
          </>
        }
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Input
            allowClear
            placeholder="e.g. LOAN-2024-05"
            value={changeRefInput}
            onChange={(event) => setChangeRefInput(event.target.value)}
            style={INPUT_STYLE}
            maxLength={40}
          />
        </Space>
      </GlassModal>

      <GlassModal
        width={isMobile ? "90vw" : 400}
        open={changeEmailModalIsopen}
        zIndex={802}
        onClose={handleCloseChangeEmailModal}
        contentPadding={24}
        headerTitle="Change borrower email"
        headerSubtitle="Update where payment reminders and receipts are sent."
        headerMark="EM"
        actions={
          <>
            <Button
              onClick={handleCloseChangeEmailModal}
              style={SECONDARY_BUTTON_STYLE}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleUpdateEmail}
              style={PRIMARY_BUTTON_STYLE}
              disabled={!canUpdateEmail}
              loading={updateEmailLoading}
            >
              Update email
            </Button>
          </>
        }
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Input
            allowClear
            type="email"
            placeholder="name@email.com"
            value={changeEmailInput}
            onChange={(event) => setChangeEmailInput(event.target.value)}
            style={INPUT_STYLE}
          />
        </Space>
      </GlassModal>

      <GlassModal
        width={isDesktop ? "min(980px, 72vw)" : isMobile ? "95vw" : "80vw"}
        open={openedLoans !== null}
        zIndex={800}
        onClose={handleCloseLoanDetails}
        contentPadding={28}
        headerTitle={openedLoans !== null ? data[openedLoans].principal_name : "Manual loan details"}
        headerSubtitle={openedLoans !== null ? `${data[openedLoans].ref_code || "N/A Ref"} • ${data[openedLoans].email || "No email"}` : ""}
        headerMark={
          openedLoans !== null
            ? (data[openedLoans].principal_name || "ML").slice(0, 2).toUpperCase()
            : "ML"
        }
      >
        {openedLoans !== null ? (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "90%",
              }}
            >
              <div>
                <div
                  style={{
                    color: "#142C44CC",
                    fontSize: "18px",
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: "800",
                  }}
                >
                  {data[openedLoans].principal_name}
                </div>
                <div
                  style={{
                    color: "#142C44CC",
                    fontSize: "13px",
                    fontFamily: "Manrope, sans-serif",
                    fontWeight: "800",
                  }}
                >
                  {data[openedLoans].ref_code || "N/A Ref"}
                </div>
                <div style={{ color: "grey", fontSize: "10px" }}>
                  {"Mobile: " +
                    data[openedLoans].mobile +
                    " Email: " +
                    data[openedLoans].email}
                </div>
                <div style={{ color: "grey", fontSize: "10px" }}>
                  Giro Status:{" "}
                  <span
                    style={{
                      color: data[openedLoans].status ? "green" : "red",
                    }}
                  >
                    {data[openedLoans].status ? "Complete" : "Not configured"}
                  </span>
                </div>
                {/* <div>
                  <div>
                    Loan Amount:{" "}
                    {getTotalRepayment(
                      data[openedLoans].repayments
                    )?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "SGD",
                    })}
                  </div>
                  <div></div>
                </div> */}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                  width: "50%",
                  justifyContent: "flex-end",
                }}
              >
                <div
                  onClick={() => {
                    setChangeRefModalIsOpen(true);
                    setChangeRefInput(data[openedLoans].ref_code);
                  }}
                  className="mui_button sl-manual-btn sl-manual-btn-primary"
                  style={{
                    padding: "8px 25px",
                    borderRadius: "8px",
                    color: "white",
                    backgroundColor: PRIMARY_COLOR,
                  }}
                >
                  Edit Ref
                </div>
                <div
                  onClick={() => {
                    setChangeEmailModalIsOpen(true);
                  }}
                  className="mui_button sl-manual-btn sl-manual-btn-primary"
                  style={{
                    padding: "8px 25px",
                    borderRadius: "8px",
                    color: "white",
                    backgroundColor: PRIMARY_COLOR,
                  }}
                >
                  Update Email
                </div>
                <div
                  onClick={() => {
                    deleteManualLoan(data[openedLoans].id);
                  }}
                  className="mui_button sl-manual-btn sl-manual-btn-danger"
                  style={{
                    padding: "8px 25px",
                    borderRadius: "8px",
                    color: "white",
                    backgroundColor: PRIMARY_COLOR,
                  }}
                >
                  Delete
                </div>
                {!data[openedLoans].status ? (
                  <div
                    onClick={() => {
                      if (viewingTab === 0) {
                        setViewingTab(1);
                      } else {
                        setViewingTab(0);
                      }
                    }}
                    className="mui_button sl-manual-btn sl-manual-btn-primary"
                    style={{
                      padding: "8px 25px",
                      borderRadius: "8px",
                      color: "white",
                      backgroundColor: PRIMARY_COLOR,
                    }}
                  >
                    {viewingTab === 0 ? "Setup Giro" : "View schedule"}
                  </div>
                ) : null}
              </div>
            </div>
            <div
              style={{
                width: "90%",
                height: 1,
                background: "lightgrey",
                margin: "10px 0px",
              }}
            />
            <div style={{ display: "flex" }}>
              <div
                style={{
                  fontSize: "10px",
                  color: "grey",
                  marginRight: "5px",
                  marginTop: "2px",
                }}
              >
                Loan Amount:
              </div>
              <div
                style={{
                  marginRight: "30px",
                  color: "#142C44CC",
                  fontSize: "16px",
                  fontFamily: "Manrope, sans-serif",
                  fontWeight: "600",
                }}
              >
                {data[openedLoans]?.loanAmount === null
                  ? "SGD 0.00"
                  : parseFloat(data[openedLoans]?.loanAmount)?.toLocaleString(
                      "en-US",
                      {
                        style: "currency",
                        currency: "SGD",
                      }
                    )}
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "grey",
                  marginRight: "5px",
                  marginTop: "2px",
                }}
              >
                Interest Rate:
              </div>
              <div
                style={{
                  marginRight: "30px",
                  color: "#142C44CC",
                  fontSize: "16px",
                  fontFamily: "Manrope, sans-serif",
                  fontWeight: "600",
                }}
              >
                {(data[openedLoans]?.interest === null
                  ? 0
                  : data[openedLoans]?.interest) + " %"}
                {" per month"}
              </div>
            </div>
            <div style={{ display: "flex", marginTop: "5px" }}>
              <div
                style={{
                  fontSize: "10px",
                  color: "grey",
                  marginRight: "5px",
                  marginTop: "2px",
                }}
              >
                Processing Fee:
              </div>
              <div
                style={{
                  marginRight: "30px",
                  color: "#142C44CC",
                  fontSize: "16px",
                  fontFamily: "Manrope, sans-serif",
                  fontWeight: "600",
                }}
              >
                {(data[openedLoans]?.processingFee === null
                  ? 0
                  : data[openedLoans]?.processingFee) + " %"}
              </div>
              <div
                style={{
                  fontSize: "10px",
                  color: "grey",
                  marginRight: "5px",
                  marginTop: "2px",
                }}
              >
                Disbursement Date:
              </div>
              <div
                style={{
                  marginRight: "30px",
                  color: "#142C44CC",
                  fontSize: "16px",
                  fontFamily: "Manrope, sans-serif",
                  fontWeight: "600",
                }}
              >
                {data[openedLoans]?.start_date === null
                  ? 0
                  : formatDate(
                      new Date(
                        getPreviousMonthDate(data[openedLoans]?.start_date)
                      )
                    )}
              </div>
            </div>
            {viewingTab === 1 ? (
              <div style={{ marginTop: "15px" }}>
                <div style={{}}>
                  <div style={{ width: "100%" }}>
                    <div>Select Bank Account Type</div>
                    <select
                      value={chargeData.payerSegment}
                      onChange={(e) => {
                        setChargeData({
                          ...chargeData,
                          payerSegment: e.target.value,
                        });
                        console.log(chargeData);
                      }}
                      style={{
                        width: isMobile ? "100%" : "50%",
                        borderRadius: "7px",
                        outline: "none",
                        border: "1px solid lightgrey",
                        padding: "5px 8px",
                      }}
                    >
                      <option value={""}>Bank Account Type</option>
                      <option value={"RETAIL"}>Personal</option>
                      <option value={"CORPORATE"}>Corporate</option>
                    </select>
                  </div>
                  {chargeData.payerSegment === "RETAIL" ? (
                    <div style={{ width: "100%", marginTop: "10px" }}>
                      <div>Select Bank</div>
                      <select
                        value={chargeData.payerSwiftBic}
                        onChange={(e) => {
                          setChargeData({
                            ...chargeData,
                            payerSwiftBic: e.target.value,
                          });
                          console.log(chargeData);
                        }}
                        style={{
                          width: isMobile ? "100%" : "50%",
                          borderRadius: "7px",
                          outline: "none",
                          border: "1px solid lightgrey",
                          padding: "5px 8px",
                        }}
                      >
                        <option value={""}>Select Bank</option>
                        <option value={"BKCHSGSGXXX"}>Bank of China</option>
                        <option value={"DBSSSGSGXXX"}>DBS Bank</option>
                        <option value={"HSBCSGS2XXX"}>HSBC Bank</option>
                        <option value={"ICBKSGSGXXX"}>
                          Industrial & Commercial Bank of China
                        </option>
                        <option value={"MBBESGS2XXX"}>Maybank</option>
                        <option value={"OCBCSGSGXXX"}>
                          Oversea-Chinese Banking Corporation
                        </option>
                        <option value={"SCBLSG22XXX"}>
                          Standard Chartered Bank
                        </option>
                        <option value={"UOVBSGSGXXX"}>
                          United Overseas Bank
                        </option>
                      </select>
                    </div>
                  ) : null}
                  {chargeData.payerSegment === "CORPORATE" ? (
                    <div style={{ width: "100%", marginTop: "10px" }}>
                      <div>Select Bank</div>
                      <select
                        value={chargeData.payerSwiftBic}
                        onChange={(e) => {
                          setChargeData({
                            ...chargeData,
                            payerSwiftBic: e.target.value,
                          });
                          console.log(chargeData);
                        }}
                        style={{
                          width: "50%",
                          borderRadius: "7px",
                          outline: "none",
                          border: "1px solid lightgrey",
                          padding: "5px 8px",
                        }}
                      >
                        <option value={""}>Select Bank</option>
                        <option value={"BKCHSGSGXXX"}>Bank of China</option>
                        <option value={"DBSSSGSGXXX"}>DBS Bank</option>
                        <option value={"SSPISGSGXXX"}>
                          MariBank Singapore
                        </option>
                        <option value={"MBBESGS2XXX"}>Maybank</option>
                        <option value={"OCBCSGSGXXX"}>
                          Oversea-Chinese Banking Corporation
                        </option>
                        <option value={"UOVBSGSGXXX"}>
                          United Overseas Bank
                        </option>
                      </select>
                    </div>
                  ) : null}
                  <div style={{ width: "100%", marginTop: "10px" }}>
                    <div>Account Holder Name</div>
                    <input
                      placeholder="Account Holder Name"
                      value={chargeData.payerName}
                      onChange={(e) => {
                        setChargeData({
                          ...chargeData,
                          payerName: e.target.value,
                        });
                        console.log(chargeData);
                      }}
                      style={{
                        width: "50%",
                        borderRadius: "7px",
                        outline: "none",
                        border: "1px solid lightgrey",
                        padding: "5px 8px",
                      }}
                    />
                  </div>
                  <div style={{ width: "100%", marginTop: "10px" }}>
                    <div>Bank Account Number</div>
                    <input
                      placeholder="Bank Account Number"
                      value={chargeData.accountNumber}
                      onChange={(e) => {
                        setChargeData({
                          ...chargeData,
                          accountNumber: e.target.value,
                        });
                        console.log(chargeData);
                      }}
                      style={{
                        width: "50%",
                        borderRadius: "7px",
                        outline: "none",
                        border: "1px solid lightgrey",
                        padding: "5px 8px",
                      }}
                    />
                  </div>
                  <div style={{ width: "100%", marginTop: "10px" }}>
                    <div>Payment End Date</div>
                    <input
                      type="date"
                      disabled
                      value={chargeData.endDate}
                      onChange={(e) => {
                        setChargeData({
                          ...chargeData,
                          endDate: e.target.value,
                        });
                        console.log(chargeData);
                      }}
                      style={{
                        width: "50%",
                        borderRadius: "7px",
                        outline: "none",
                        border: "1px solid lightgrey",
                        padding: "5px 8px",
                      }}
                    />
                  </div>
                  <div style={{ width: "100%", marginTop: "10px" }}>
                    <div>Charge Limit</div>
                    <input
                      type="number"
                      value={chargeData.precheckMaxAmount}
                      min={1}
                      disabled
                      max={1000000}
                      onChange={(e) => {
                        setChargeData({
                          ...chargeData,
                          precheckMaxAmount: parseFloat(e.target.value),
                        });
                        console.log(chargeData);
                      }}
                      style={{
                        width: "50%",
                        borderRadius: "7px",
                        outline: "none",
                        border: "1px solid lightgrey",
                        padding: "5px 8px",
                      }}
                    />
                  </div>
                  <div style={{ display: "flex" }}>
                    <div
                      className="sl-manual-btn sl-manual-btn-primary"
                      onClick={() => {
                        creategiroauth();
                      }}
                      style={{
                        background: PRIMARY_COLOR,
                        color: "white",
                        padding: "7px 30px",
                        border: "none",
                        marginTop: "10px",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                    >
                      Authorize
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  marginTop: "15px",
                }}
              >
                <div
                  style={{
                    borderRadius: "7px",
                    overflow: "hidden",
                    boxShadow: "0px 0px 10px rgba(0,0,0,0.3)",
                  }}
                >
                  {data[openedLoans]?.repayments?.map((repa, index) => (
                    <div
                      onClick={() => setClickedPaymentRow(index)}
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "5px 20px",
                        background:
                          repa.status === 0 &&
                          new Date(repa.payment_date) < new Date()
                            ? "red"
                            : index % 2
                            ? "rgba(112, 87, 246, 0.10)"
                            : "white",
                        color:
                          repa.status === 0 &&
                          new Date(repa.payment_date) < new Date()
                            ? "white"
                            : "#142C44CC",
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ width: "25%" }}>
                        {parseFloat(repa.amount)?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "SGD",
                        })}
                      </div>
                      <div style={{ width: "15%" }}>
                        {formatDate(new Date(repa.payment_date))}
                      </div>
                      <div style={{ width: "15%" }}>
                        {formatDate(new Date(repa.date))}
                      </div>
                      <div style={{ width: "35%", textAlign: "right" }}>
                        {DateDifference(repa)}
                        {repa.status
                          ? "paid"
                          : new Date(repa.payment_date) < new Date()
                          ? "late"
                          : "unpaid"}
                      </div>
                    </div>
                  ))}
                </div>
                {clickedPaymentRow !== null &&
                data[openedLoans].repayments[clickedPaymentRow].status !== 1 ? (
                  <div style={{ marginTop: "20px" }}>
                    <div
                      style={{
                        color: "#142C44CC",
                        fontSize: "18px",
                        fontFamily: "Manrope, sans-serif",
                        fontWeight: "800",
                      }}
                    >
                      Payment {clickedPaymentRow + 1} of{" "}
                      {data[openedLoans]?.repayments.length}
                    </div>

                    <div style={{ color: "grey", fontSize: "10px" }}>
                      Please confirm that you would like to mark the selected
                      payment as complete.
                    </div>

                    <div>
                      <input
                        type="date"
                        style={{
                          width: "80%",
                          padding: "4px 8px",
                          borderRadius: "5px",
                          border: "1px solid lightgrey",
                          outline: "none",
                        }}
                        value={
                          data[openedLoans].repayments[clickedPaymentRow]
                            .date || ""
                        }
                        onChange={(e) => {
                          const updatedRepayments = [
                            ...data[openedLoans].repayments,
                          ];
                          updatedRepayments[clickedPaymentRow].date =
                            e.target.value;

                          const updatingData = [...data];
                          updatingData[openedLoans].repayments =
                            updatedRepayments;
                          setData(updatingData);
                        }}
                      />
                    </div>

                    {data[openedLoans].repayments[clickedPaymentRow].date ? (
                      <div style={{ display: "flex", marginTop: "15px" }}>
                        <div
                          onClick={() => {
                            const updatedRepayments = [
                              ...data[openedLoans].repayments,
                            ];
                            updatedRepayments[clickedPaymentRow].status = 1;
                            const dataToPush = {
                              id: data[openedLoans].id,
                              repayments: JSON.stringify(updatedRepayments),
                              amount: parseFloat(
                                data[openedLoans].repayments[clickedPaymentRow]
                                  .amount
                              ),
                            };
                            // console.log(data[openedLoans].repayments[clickedPaymentRow].amount)
                            updateLoanPaymentStatus(dataToPush);
                          }}
                          className="mui_button sl-manual-btn sl-manual-btn-primary"
                          style={{
                            padding: "8px 25px",
                            borderRadius: "8px",
                            color: "white",
                            backgroundColor: PRIMARY_COLOR,
                          }}
                        >
                          Complete
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ) : null}
      </GlassModal>
      <div
        className="sl-manual-page-wrap"
        style={{
          width: "97%",
          height: "100%",
          padding: "4px 0px 20px 20px",
        }}
      >
        <div
          className="sl-manual-panel"
          style={{
            height: "100%",
            borderRadius: 24,
            background: GLASS_CARD_BACKGROUND,
            padding: "24px",
            border: CARD_BORDER,
            boxShadow: CARD_SHADOW,
            backdropFilter: "blur(12px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            className="sl-manual-head"
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  color: PRIMARY_COLOR,
                  fontSize: "18px",
                  fontFamily: "Manrope, sans-serif",
                  fontWeight: "800",
                }}
              >
                Manual Giro Setup
              </div>
              <div
                style={{
                  color: "rgba(112,87,246,0.7)",
                  fontFamily: "Manrope, sans-serif",
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                To create a new Giro setup, hit the create new button to begin.
              </div>
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              <div
                onClick={() => exportToExcel()}
                className="mui_button sl-manual-btn sl-manual-btn-secondary"
                style={{
                  padding: "8px 25px",
                  borderRadius: "8px",
                  color: "white",
                  backgroundColor: PRIMARY_COLOR,
                }}
              >
                Export to Excel
              </div>
              <div
                onClick={() => setCreateLoanModalIsOpen(true)}
                className="mui_button sl-manual-btn sl-manual-btn-primary"
                style={{
                  padding: "8px 25px",
                  borderRadius: "8px",
                  color: "white",
                  backgroundColor: PRIMARY_COLOR,
                }}
              >
                Create New
              </div>
            </div>
          </div>

          <div
            className="sl-manual-table-card"
            style={{
              background: GLASS_CARD_BACKGROUND,
              borderRadius: 22,
              width: "100%",
              height: "35vh",
              color: PRIMARY_COLOR,
              overflow: "hidden",
              marginTop: 15,
              border: CARD_BORDER,
              boxShadow: CARD_SHADOW,
              backdropFilter: "blur(10px)",
              padding: "12px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              className="sl-manual-table-head"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "14px 20px",
                marginBottom: 8,
                background:
                  "linear-gradient(90deg, rgba(112,87,246,0.18), rgba(255,255,255,0.4))",
                color: PRIMARY_COLOR,
                fontSize: 14,
                fontFamily: "Manrope, sans-serif",
                fontWeight: 700,
                gap: "1%",
                borderRadius: 16,
                border: CARD_BORDER,
                boxShadow: "0 12px 24px rgba(112,87,246,0.08)",
                backdropFilter: "blur(8px)",
              }}
            >
              <div style={{ width: "35%" }}>Principal Name</div>
              <div
                onClick={() => {
                  if (filterBy === "start") {
                    setFilterBy("");
                    console.log("");
                  } else {
                    setFilterBy("start");
                    console.log("start");
                  }
                }}
                style={{ width: "15%", cursor: "pointer" }}
              >
                Loan start date
              </div>
              <div style={{ width: "15%" }}>Monthly-installment</div>
              <div
                onClick={() => {
                  if (filterBy === "current") {
                    setFilterBy("");
                    console.log("");
                  } else {
                    setFilterBy("current");
                    console.log("current");
                  }
                }}
                style={{ width: "15%", cursor: "pointer" }}
              >
                Current due-date
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
              {data
                .filter(
                  (user) =>
                    user.principal_name
                      ?.toLowerCase()
                      .includes(paramValue?.toLowerCase()) ||
                    user.ref_code
                      ?.toLowerCase()
                      .includes(paramValue?.toLowerCase()) ||
                    user.email
                      ?.toLowerCase()
                      .includes(paramValue?.toLowerCase())
                )
                .sort((a, b) => {
                  if (filterBy === "start") {
                    return new Date(a.start_date) - new Date(b.start_date);
                  } else if (filterBy === "current") {
                    const aCurrent = new Date(
                      a.repayments?.find((repay) => repay.status === 0)
                        ?.payment_date || 0
                    );
                    const bCurrent = new Date(
                      b.repayments?.find((repay) => repay.status === 0)
                        ?.payment_date || 0
                    );
                    return aCurrent - bCurrent;
                  }
                  return 0; // No sorting if filterBy is ""
                })
                .map((pay, index) => {
                  const nextPaymentDate = getNextRepaymentDate(pay);
                  const hasOutstandingRepayment = Boolean(nextPaymentDate);
                  const overdue = hasOutstandingRepayment
                    ? isLoanOverdue(pay)
                    : false;

                  return (
                    <div
                      key={index}
                      onClick={() => {
                        const dataIndex = data.findIndex(
                          (dat) => dat.id === pay.id
                        );
                        if (dataIndex > -1) {
                          setOpenedLoans(dataIndex);
                        }
                        setChargeData({
                          ...chargeData,
                          id: pay.id,
                          endDate: pay.end_date,
                          precheckMaxAmount: pay.amount,
                        });
                      }}
                      className="row_click sl-manual-row"
                      style={getManualLoanRowStyle(pay, overdue)}
                    >
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          background: overdue
                            ? "linear-gradient(90deg, rgba(231,76,60,0.6), rgba(231,76,60,0))"
                            : "linear-gradient(90deg, rgba(112,87,246,0.35), rgba(112,87,246,0))",
                          opacity: 0.18,
                          pointerEvents: "none",
                        }}
                      />
                      <div
                        style={{
                          position: "relative",
                          zIndex: 1,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                          gap: "1%",
                          color: "#14213F"
                        }}
                      >
                        <div
                          style={{
                            width: "35%",
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                          }}
                        >
                          <div style={{ fontWeight: 700, fontSize: "16px" }}>
                            {pay?.principal_name}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                              opacity: overdue ? 0.9 : 0.8,
                            }}
                          >
                            <div>{pay?.email}</div>
                            <div>
                              {pay.ref_code ? `- ${pay?.ref_code}` : ""}
                            </div>
                          </div>
                        </div>
                        <div style={{ width: "15%", fontWeight: 600 }}>
                          {formatDate(new Date(pay?.start_date))}
                        </div>
                        <div style={{ width: "15%", fontWeight: 600 }}>
                          {parseFloat(pay?.amount)?.toLocaleString("en-US", {
                            style: "currency",
                            currency: "SGD",
                          })}
                        </div>
                        <div
                          style={{
                            width: "15%",
                            fontWeight: 600,
                            color: hasOutstandingRepayment
                              ? overdue
                                ? "#14213F"
                                : "rgba(112,87,246,0.75)"
                              : "rgba(46,204,113,0.85)",
                          }}
                        >
                          {hasOutstandingRepayment && nextPaymentDate
                            ? formatDate(nextPaymentDate)
                            : "Payment Completed"}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "20px",
              }}
            >
              <div
                className="sl-manual-egiro-title"
                style={{
                  color: TEXT_COLOR,
                  fontSize: "20px",
                  fontFamily: "Manrope,sans-serif",
                  fontWeight: 800,
                }}
              >
                Pending E-Giro Transactions
              </div>
              <div
                className="sl-manual-btn sl-manual-btn-primary"
                onClick={(e) => {
                  e.preventDefault();
                  if (checkboxChecked.length < 1) {
                    message.error("No transaction selected.");
                  } else {
                    generateFixedWidthTxt();
                  }
                }}
                style={{
                  background: PRIMARY_COLOR,
                  color: "white",
                  padding: "7px 30px",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                }}
              >
                Export GIRO
              </div>
            </div>
            <div
              className="sl-manual-table-card"
              style={{
                background: GLASS_CARD_BACKGROUND,
                borderRadius: 22,
                width: "100%",
                height: "35vh",
                color: PRIMARY_COLOR,
                overflow: "hidden",
                marginTop: 15,
                border: CARD_BORDER,
                boxShadow: CARD_SHADOW,
                backdropFilter: "blur(10px)",
                padding: "12px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                className="sl-manual-table-head"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "14px 20px",
                  marginBottom: 8,
                  background:
                    "linear-gradient(90deg, rgba(112,87,246,0.18), rgba(255,255,255,0.4))",
                  color: PRIMARY_COLOR,
                  fontSize: 14,
                  fontFamily: "Manrope, sans-serif",
                  fontWeight: 700,
                  borderRadius: 16,
                  border: CARD_BORDER,
                  boxShadow: "0 12px 24px rgba(112,87,246,0.08)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <div style={{ width: "50%" }}>Entity Name</div>
                <div style={{ width: "15%" }}>Repayment Amount</div>
                <div style={{ width: "15%" }}>Payment Date</div>
              </div>
              <div style={{ flex: 1, overflowY: "auto", paddingRight: 4 }}>
                {data
                  .filter(
                    (dat) =>
                      dat.status === 1 &&
                      dat.repayments.filter((repa) => repa.status === 0)
                        .length > 0
                  )
                  .map((pay, index) => (
                    <div
                      key={index}
                      className="sl-manual-row"
                      style={{
                        position: "relative",
                        overflow: "hidden",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "16px 20px",
                        margin: "12px 0",
                        background: GLASS_CARD_BACKGROUND,
                        color: PRIMARY_COLOR,
                        fontFamily: "Manrope, sans-serif",
                        fontWeight: 500,
                        fontSize: 12,
                        borderRadius: 16,
                        border: CARD_BORDER,
                        boxShadow: CARD_SHADOW,
                        backdropFilter: "blur(8px)",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(90deg, rgba(112,87,246,0.35), rgba(112,87,246,0))",
                          opacity: 0.12,
                          pointerEvents: "none",
                        }}
                      />
                      <div
                        style={{
                          position: "relative",
                          zIndex: 1,
                          display: "flex",
                          alignItems: "center",
                          width: "50%",
                          gap: "10px",
                        }}
                      >
                        <input
                          type="checkbox"
                          style={{
                            width: "15px",
                            height: "15px",
                            marginBottom: "0px",
                          }}
                          checked={checkboxChecked.includes(index)}
                          onChange={() => handleCheckboxChange(index)}
                        />
                        {pay?.principal_name}
                      </div>
                      <div
                        style={{
                          position: "relative",
                          zIndex: 1,
                          width: "15%",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          fontWeight: 600,
                        }}
                      >
                        {parseFloat(pay?.amount)?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "SGD",
                        })}
                      </div>
                      <div
                        style={{
                          position: "relative",
                          zIndex: 1,
                          width: "15%",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          fontWeight: 600,
                        }}
                      >
                        {formatDate(
                          new Date(
                            pay?.repayments?.filter(
                              (repa) => repa.status === 0
                            )?.[0].payment_date
                          )
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestCharge;
